import { scriptLoadAY } from '../utils/ad-ops/scriptLoadAY'
import { useCpms } from '../composables/ad-ops/cpms/useCpms'
import debug from '../utils/console'
import type { AnalyticsProps, Slot } from '../types/assertive'
import {
  getCPMMap,
  updateFloorSlotTargeting,
} from '../utils/ad-ops/dynamicFloor'
import { useRevContentStore } from '../store/ad-ops/revContentStore'
import { OpenFeature } from '@openfeature/web-sdk'

const logger = debug.withTag('plugins/assertive.client')

export default defineNuxtPlugin({
  name: 'assertive',
  dependsOn: ['openfeature'],
  setup: async (nuxtApp) => {
    const { client } = await useOpenFeature()
    const { track } = useAnalytics()

    const { ayEntityId } = useRuntimeConfig().public
    const layoutName = client.getStringValue('splittestname', 'control')
    const layoutSkeleton = client.getStringValue('layoutSkeleton', 'default')

    const adSettingsStore = useAdSettingsStore()
    await adSettingsStore.initialized
    const { data: adSettings } = storeToRefs(adSettingsStore)

    const revContentStore = useRevContentStore()
    await revContentStore.init()

    const isDynamicFloorEnabled = computed(
      () => adSettings.value.adsDynamicFloorPricing?.isEnabled
    )
    const flooredList = computed(() =>
      Array.from(
        adSettings.value.adsDynamicFloorPricing?.flooredPlacements ?? []
      )
    )
    const analyticsProps: AnalyticsProps = {
      ntp_app_version: getNtpAppVersion(),
      host_browser_app_version: await getHostBrowserAppVersion(),
      type_tag: await getTypeTag(),
    }

    const openFeatureContext = OpenFeature.getContext()

    const ayLoaded = await scriptLoadAY(
      ayEntityId,
      analyticsProps,
      isDynamicFloorEnabled.value,
      layoutName,
      layoutSkeleton,
      typeof openFeatureContext.cohort === 'string'
        ? openFeatureContext.cohort
        : null
    )

    if (!ayLoaded.loading) {
      logger.error('Assertive Yield SDK failed to load')
      return
    }

    logger.info('Assertive Yield loaded')

    const { getHourlyCpmForPlacement } = useCpms()

    const cpmMap = await getCPMMap(
      isDynamicFloorEnabled.value,
      flooredList.value,
      getHourlyCpmForPlacement
    )

    // update targeting for each slot -KVs in GAM360 MUCH match
    window.ayManagerEnv.cmd.push(() => {
      window.ayManagerEnv.onEvent(
        'afterDefineSlot',
        (adUnitInstanceCode: string, slot: Slot) => {
          updateFloorSlotTargeting(
            adUnitInstanceCode,
            slot,
            cpmMap,
            isDynamicFloorEnabled.value
          )
        }
      )
    })

    window.ayManagerEnv.cmd.push(() => {
      window.ayManagerEnv.onEvent(
        'afterRefresh',
        (adUnitInstanceCode: string[]) => {
          adUnitInstanceCode.forEach((code) => {
            const eventData: NtpAdEvent = {
              ntp_tile_position: -1,
              ntp_ad_provider: 'programmatic',
              ntp_creative_id: '',
              ntp_placement_name: stripAYCode(code),
              ntp_ad_brand: '',
              ntp_ad_clickurl: '',
              ntp_ad_refresh: true,
            }
            track('NtpAd_Rendered', eventData)
          })
        }
      )
    })

    if (!window.analytics) {
      window.analytics = useAnalytics()
    }

    nuxtApp.provide('ayLoaded', ayLoaded)
    nuxtApp.provide('assertive', window.assertive)
  },
})
